import { CalendarIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Button, Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import type { tRetentionSessionCta } from '../../CancelSubscription.types';
import { tNotSeeingResultsConfirmationModalProps } from '../ConfirmationModal.types';

// Each description matches the CTA it sits above; with no CTA, the copy must not
// point at a button that is not there.
const WHATS_NEXT_DESCRIPTION_KEYS: Record<
  tRetentionSessionCta['kind'],
  string
> = {
  booking: 'cancelSubscription.confirmationResults.whatsNextDescription',
  none: 'cancelSubscription.confirmationResults.whatsNextUnavailableDescription',
};

export default function NotSeeingResultsConfirmationModal(
  props: tNotSeeingResultsConfirmationModalProps,
) {
  const {
    isOpen,
    nextInvoiceDate,
    formatDate,
    onScheduleConsultation,
    sessionCta,
    durationDays,
    onClose,
  } = props;
  const { t } = useTranslation('settings/SubscriptionManager');
  const { currentUser } = useUserContext();

  return (
    <Modal open={isOpen} className="max-w-[631px] rounded-lg p-0">
      <Modal.Header className="flex flex-row justify-end p-4 pb-0 mb-0">
        <Button
          color="ghost"
          size="sm"
          shape="circle"
          onClick={onClose}
          aria-label={t('cancelSubscription.offer.close')}
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      </Modal.Header>
      <Modal.Body className="pt-0 px-6 pb-6 font-sans">
        <div className="flex flex-col gap-3 items-center">
          {/* Success Icon */}
          <img
            src="/images/subscriptions/retention-success.svg"
            alt="Results Program Activated"
            className="w-[199px] h-auto"
          />

          {/* Heading */}
          <div className="flex flex-col text-center">
            <h4 className="text-[28px] font-bold text-success">
              <Trans
                i18nKey="settings/SubscriptionManager:cancelSubscription.confirmationResults.heading"
                values={{ name: currentUser?.name.split(' ')[0] }}
              />
            </h4>
            <p className="text-[28px] font-bold text-success">
              {t('cancelSubscription.confirmationResults.subheading')}
            </p>
          </div>

          {/* Activated Features Section */}
          <div className="w-full">
            <p className="text-lg font-bold mb-3">
              {t('cancelSubscription.confirmationResults.activatedTitle')}
            </p>
            <div className="flex flex-col gap-1">
              {/* Consultation Sessions Detail */}
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <p className="text-base font-bold">
                    {t(
                      'cancelSubscription.confirmationResults.consultationDetail',
                    )}
                  </p>
                  <div className="ml-0 flex flex-col gap-1">
                    <p className="text-sm text-neutral-600">
                      •{' '}
                      {t(
                        'cancelSubscription.confirmationResults.consultationDetail1',
                      )}
                    </p>
                    <p className="text-sm text-neutral-600">
                      •{' '}
                      {t(
                        'cancelSubscription.confirmationResults.consultationDetail2',
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Days Detail — the applied extension's own days */}
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <p className="text-base font-bold">
                    {durationDays !== null
                      ? t(
                          'cancelSubscription.confirmationResults.additionalDays',
                          { days: durationDays },
                        )
                      : t(
                          'cancelSubscription.confirmationResults.additionalDaysUnspecified',
                        )}
                  </p>
                  <div className="ml-0 flex flex-col gap-1">
                    <p className="text-sm text-neutral-600">
                      •{' '}
                      {t(
                        'cancelSubscription.confirmationResults.additionalDaysStart',
                      )}
                    </p>
                    <p className="text-sm text-neutral-600">
                      •{' '}
                      {t('cancelSubscription.confirmationResults.nextInvoice', {
                        date: formatDate(nextInvoiceDate),
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* What's Next Section */}
          <div className="flex flex-col gap-4 w-full bg-success/5 rounded-lg px-8 py-6">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 flex-shrink-0" />
              <p className="text-lg font-bold">
                {t('cancelSubscription.confirmationResults.whatsNext')}
              </p>
            </div>
            <p className="text-base text-neutral-700">
              {t(WHATS_NEXT_DESCRIPTION_KEYS[sessionCta.kind])}
            </p>

            {/* A button that swaps the modal for the embedded scheduler; with
                no configured booking page, no CTA at all. */}
            {sessionCta.kind === 'booking' && (
              <Button
                color="primary"
                className="w-full rounded-[4px]"
                onClick={onScheduleConsultation}
                data-testid="not-seeing-results-confirmation-schedule-button"
              >
                {t('cancelSubscription.confirmationResults.scheduleButton')}
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
